import {
  customerPageSchema,
  customerSchema,
  customerSegmentationSummarySchema,
  customerTagDefinitionSchema,
  customerTagsSchema,
  customerTimelineSchema,
  duplicatePhoneDetailsSchema,
  mergeResultSchema,
  type CreateCustomerInput,
  type Customer,
  type CustomerOrderFilter,
  type CustomerPage,
  type CustomerSegment,
  type CustomerSegmentationSummary,
  type CustomerSortField,
  type CreateCustomerTagInput,
  type CustomerTagDefinition,
  type CustomerTagList,
  type CustomerTimeline,
  type DuplicatePhoneDetails,
  type MergeResult,
  type UpdateCustomerTagInput,
  type UpdateCustomerInput,
} from '@tailoring-crm/shared';
import { API_BASE_URL } from '../api/base-url.js';
import type { ApiClient } from '../api/client.js';
import { isApiRequestError } from '../api/errors.js';

/**
 * Every customer call the app makes, through the one API client the shell provides — so each
 * gets credentials, the transparent refresh-and-retry, and response validation against the
 * SAME schemas the server validated its input with.
 */

export interface CustomerSearchParams {
  q?: string;
  tags?: readonly string[];
  segment?: CustomerSegment;
  /** One staff member's customers (a user id), or `unassigned`. */
  assignedTo?: string;
  /** `none`: never ordered — the enquiries. `some`: ordered at least once. */
  orders?: CustomerOrderFilter;
  sortBy?: CustomerSortField;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export async function searchCustomers(
  client: ApiClient,
  params: CustomerSearchParams = {},
): Promise<CustomerPage> {
  return await client.get('/customers', {
    schema: customerPageSchema,
    query: {
      ...(params.q ? { q: params.q } : {}),
      ...(params.tags?.length ? { tags: [...params.tags] } : {}),
      ...(params.segment ? { segment: params.segment } : {}),
      ...(params.assignedTo ? { assignedTo: params.assignedTo } : {}),
      ...(params.orders ? { orders: params.orders } : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortDir ? { sortDir: params.sortDir } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
    },
  });
}

/**
 * The five headline counts for the whole book. Deliberately takes no filter: the tiles answer a
 * question about the shop, not about whatever is typed in the search box, so they must not
 * refetch on every keystroke.
 */
export async function getCustomerSegmentation(
  client: ApiClient,
): Promise<CustomerSegmentationSummary> {
  return await client.get('/customers/summary', { schema: customerSegmentationSummarySchema });
}

/**
 * Where the export button points.
 *
 * A URL rather than a request, because the button is a plain `<a href>` and the browser's own
 * download machinery fetches it — a script-driven fetch-and-blob save silently does nothing
 * inside the Android WebView. Auth is cookie-based, so the link carries no token.
 *
 * It carries the SAME filter the list is showing, so the file matches the screen.
 */
export function customerExportPath(params: CustomerSearchParams = {}): string {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  for (const tag of params.tags ?? []) search.append('tags', tag);
  if (params.segment) search.set('segment', params.segment);
  if (params.assignedTo) search.set('assignedTo', params.assignedTo);
  if (params.orders) search.set('orders', params.orders);
  if (params.sortBy) search.set('sortBy', params.sortBy);
  if (params.sortDir) search.set('sortDir', params.sortDir);
  const query = search.toString();
  return `${API_BASE_URL}/customers/export${query ? `?${query}` : ''}`;
}

export async function getCustomer(client: ApiClient, customerId: string): Promise<Customer> {
  return await client.get(`/customers/${customerId}`, { schema: customerSchema });
}

/** Everything the shop has done for one customer, already merged and ordered by the API. */
export async function getCustomerTimeline(
  client: ApiClient,
  customerId: string,
): Promise<CustomerTimeline> {
  return await client.get(`/customers/${customerId}/timeline`, { schema: customerTimelineSchema });
}

export async function createCustomer(
  client: ApiClient,
  input: CreateCustomerInput,
): Promise<Customer> {
  return await client.post('/customers', input, { schema: customerSchema });
}

export async function updateCustomer(
  client: ApiClient,
  customerId: string,
  patch: UpdateCustomerInput,
): Promise<Customer> {
  return await client.patch(`/customers/${customerId}`, patch, { schema: customerSchema });
}

export async function deleteCustomer(client: ApiClient, customerId: string): Promise<void> {
  await client.del(`/customers/${customerId}`);
}

/** The shop's managed tags, with their colours and customer counts, and the most it may have. */
export async function listCustomerTags(client: ApiClient): Promise<CustomerTagList> {
  return await client.get('/customers/tags', { schema: customerTagsSchema });
}

export async function createCustomerTag(
  client: ApiClient,
  input: CreateCustomerTagInput,
): Promise<CustomerTagDefinition> {
  return await client.post('/customers/tags', input, { schema: customerTagDefinitionSchema });
}

export async function updateCustomerTag(
  client: ApiClient,
  tagId: string,
  patch: UpdateCustomerTagInput,
): Promise<CustomerTagDefinition> {
  return await client.patch(`/customers/tags/${tagId}`, patch, {
    schema: customerTagDefinitionSchema,
  });
}

export async function deleteCustomerTag(client: ApiClient, tagId: string): Promise<void> {
  await client.del(`/customers/tags/${tagId}`);
}

export async function mergeCustomers(
  client: ApiClient,
  input: { survivorId: string; duplicateId: string },
): Promise<MergeResult> {
  return await client.post('/customers/merge', input, { schema: mergeResultSchema });
}

/**
 * The duplicate-number refusal, if that is what this failure was.
 *
 * A 409 from the create endpoint is not an error to apologise for — it means the shop already
 * has this person, which is exactly what the counter wanted to know. Returning the parsed
 * details lets the form offer the two real choices (open them, or add anyway) instead of a
 * red message.
 */
export function duplicatePhoneOf(error: unknown): DuplicatePhoneDetails | null {
  if (!isApiRequestError(error) || error.status !== 409) return null;
  const parsed = duplicatePhoneDetailsSchema.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}
